/**
 * MaestroBath — "Load more" for collection pages
 * -----------------------------------------------------------------------------
 * Progressive enhancement over Warehouse's existing numbered pagination.
 *
 *  - The numbered <a href="?page=N"> links stay in the DOM. Googlebot does not
 *    click buttons, so those links remain the crawl path. This script never
 *    removes them, and every URL it pushes is a real, server-renderable URL.
 *  - Appends the next page's products in place, updates the "Showing x - y of z"
 *    counter, and pushes ?page=N so the address bar matches what is on screen.
 *  - Back-button: Baymard found >90% of "load more" implementations drop the
 *    user back at the top of page 1. If the shopper used the button and then
 *    comes back to a ?page=N URL, this script re-fetches pages 1..N-1, prepends
 *    them, and restores the exact scroll position.
 *  - That restore only fires when a sessionStorage marker from THIS shopper's
 *    session exists. Crawlers never have one, so a bot fetching ?page=3 sees
 *    exactly the 48 products the server rendered — no duplicate-content overlap
 *    between paginated URLs.
 *
 * Requires markup from snippets/pagination.liquid rendered with `load_more: true`.
 */
(function () {
  "use strict";

  var SECTION = '[data-section-type="collection"]';
  var PAGINATION = "[data-mb-pagination]";
  var LIST = ".product-list";
  var BUTTON = "[data-mb-load-more]";
  var COUNTERS = ".collection__products-count-showing, .collection__showing-count";
  var STORE_PREFIX = "mbLoadMore:";

  var inFlight = false;
  var restoring = false; // true while we are re-applying a remembered scroll position

  // -----------------------------------------------------------------------
  // Session state (used only to detect a genuine back-navigation)
  // -----------------------------------------------------------------------
  function stateKey() {
    var u = new URL(window.location.href);
    u.searchParams.delete("page");
    u.searchParams.delete("section_id");
    return STORE_PREFIX + u.pathname + u.search;
  }
  function readState() {
    try {
      return JSON.parse(window.sessionStorage.getItem(stateKey()) || "null");
    } catch (e) {
      return null;
    }
  }
  function writeState(obj) {
    try {
      window.sessionStorage.setItem(stateKey(), JSON.stringify(obj));
    } catch (e) {
      /* private mode / quota — the feature still works, just without restore */
    }
  }

  function currentPageFromUrl() {
    var p = parseInt(new URL(window.location.href).searchParams.get("page") || "1", 10);
    return isNaN(p) ? 1 : p;
  }

  /* Suppress the browser's own scroll restore only when we are about to change
     the document height by prepending pages. Must run before first paint. */
  if ("scrollRestoration" in history && currentPageFromUrl() > 1 && readState()) {
    history.scrollRestoration = "manual";
  }

  // -----------------------------------------------------------------------
  // Fetching
  // -----------------------------------------------------------------------
  function sectionRequestUrl(section, page) {
    var u = new URL(window.location.href);
    u.searchParams.set("page", String(page));
    u.searchParams.set("section_id", section.getAttribute("data-section-id"));
    return u.pathname + u.search;
  }

  function fetchSectionPage(section, page) {
    return fetch(sectionRequestUrl(section, page), {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" }
    }).then(function (res) {
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.text();
    }).then(function (html) {
      var host = document.createElement("div");
      host.innerHTML = html;
      return host;
    });
  }

  // -----------------------------------------------------------------------
  // DOM helpers
  // -----------------------------------------------------------------------
  function moveChildren(fromList, target, before) {
    var frag = document.createDocumentFragment();
    while (fromList.firstElementChild) frag.appendChild(fromList.firstElementChild);
    var first = frag.firstElementChild;
    if (before) target.insertBefore(frag, before);
    else target.appendChild(frag);
    return first;
  }

  function firstPageOf(pag) {
    var v = parseInt(pag.getAttribute("data-mb-first-page"), 10);
    if (!isNaN(v)) return v;
    v = parseInt(pag.getAttribute("data-current-page"), 10) || 1;
    pag.setAttribute("data-mb-first-page", String(v));
    return v;
  }

  /**
   * "Showing 49 - 144 of 286 products".
   * Derived from what is actually in the DOM, so it stays correct even if the
   * shopper jumped straight to page 3 with a numbered link and then hit "Load more".
   */
  function updateCounts(section, pag) {
    var tpl = pag.getAttribute("data-showing-template");
    var pageSize = parseInt(pag.getAttribute("data-page-size"), 10);
    var total = parseInt(pag.getAttribute("data-total-items"), 10);
    var list = section.querySelector(LIST);
    if (!tpl || !pageSize || !total || !list) return null;

    var from = (firstPageOf(pag) - 1) * pageSize + 1;
    var to = Math.min(from + list.children.length - 1, total);
    var text = tpl.replace("__MB_FROM__", String(from)).replace("__MB_TO__", String(to));
    section.querySelectorAll(COUNTERS).forEach(function (el) { el.textContent = text; });
    return { from: from, to: to, total: total };
  }

  function liveRegion(section) {
    var el = section.querySelector("[data-mb-live]");
    if (!el) {
      el = document.createElement("p");
      el.setAttribute("data-mb-live", "");
      el.setAttribute("role", "status");
      el.setAttribute("aria-live", "polite");
      el.className = "visually-hidden";
      section.appendChild(el);
    }
    return el;
  }

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) {
      if (!button.hasAttribute("data-mb-label")) button.setAttribute("data-mb-label", button.textContent.trim());
      button.textContent = button.getAttribute("data-loading-label") || "Loading…";
      button.setAttribute("aria-busy", "true");
      button.setAttribute("disabled", "disabled");
    } else {
      if (button.hasAttribute("data-mb-label")) button.textContent = button.getAttribute("data-mb-label");
      button.removeAttribute("aria-busy");
      button.removeAttribute("disabled");
    }
  }

  function showError(pag, message) {
    if (!pag) return;
    var el = pag.querySelector("[data-mb-load-more-error]");
    if (!el) {
      el = document.createElement("p");
      el.setAttribute("data-mb-load-more-error", "");
      el.setAttribute("role", "alert");
      el.className = "mb-load-more__error text--small";
      var wrap = pag.querySelector(".mb-load-more") || pag;
      wrap.appendChild(el);
    }
    el.textContent = message;
  }

  // -----------------------------------------------------------------------
  // Load more
  // -----------------------------------------------------------------------
  function loadNext(button) {
    if (inFlight) return;

    var section = button.closest(SECTION);
    var pag = button.closest(PAGINATION);
    var list = section && section.querySelector(LIST);
    if (!section || !pag || !list) return;

    var current = parseInt(pag.getAttribute("data-current-page"), 10) || 1;
    var totalPages = parseInt(pag.getAttribute("data-total-pages"), 10) || 1;
    var next = current + 1;
    if (next > totalPages) return;

    var firstPage = firstPageOf(pag);
    var hadFocus = document.activeElement === button;

    inFlight = true;
    setBusy(button, true);

    fetchSectionPage(section, next).then(function (frag) {
      var incoming = frag.querySelector(LIST);
      if (!incoming) throw new Error("no product list in response");

      moveChildren(incoming, list, null);

      /* Swap in the freshly rendered pagination so the button advances,
         and disappears by itself on the final page. */
      var newPag = frag.querySelector(PAGINATION);
      if (newPag) {
        newPag.setAttribute("data-mb-first-page", String(firstPage));
        pag.replaceWith(newPag);
        pag = newPag;
      } else {
        pag.remove();
        pag = null;
      }

      var counts = pag ? updateCounts(section, pag) : null;

      var u = new URL(window.location.href);
      u.searchParams.set("page", String(next));
      history.pushState({ mbLoadMore: next }, "", u.pathname + u.search + u.hash);
      writeState({ page: next, scrollY: window.scrollY });

      /* Warehouse recalculates the "+N more colours" swatch truncation on resize. */
      window.dispatchEvent(new Event("resize"));

      liveRegion(section).textContent = counts
        ? counts.to + " of " + counts.total + " products shown"
        : "More products loaded";

      /* Keep keyboard users where they were: the old button was removed from the
         DOM, which would otherwise dump focus back to <body>. */
      if (hadFocus && pag) {
        var newButton = pag.querySelector(BUTTON);
        if (newButton) newButton.focus({ preventScroll: true });
        else {
          var link = pag.querySelector(".pagination__nav-item");
          if (link && link.focus) link.focus({ preventScroll: true });
        }
      }
    }).catch(function () {
      setBusy(button, false);
      showError(pag, "Sorry, we couldn't load more products. Please try again.");
    }).then(function () {
      inFlight = false;
    });
  }

  // -----------------------------------------------------------------------
  // Back-navigation restore
  // -----------------------------------------------------------------------
  function restoreIfReturning() {
    var section = document.querySelector(SECTION);
    if (!section) return;
    var list = section.querySelector(LIST);
    if (!list) return;

    var page = currentPageFromUrl();
    if (page < 2) return;

    var st = readState();
    if (!st || !st.page) return; // fresh visitor or a crawler — leave the page alone

    restoring = true;

    var pag = section.querySelector(PAGINATION);
    if (pag) pag.setAttribute("data-mb-first-page", "1");

    var anchor = list.firstElementChild;
    var wanted = [];
    for (var p = 1; p < page; p++) wanted.push(p);

    Promise.all(wanted.map(function (p) {
      return fetchSectionPage(section, p).catch(function () { return null; });
    })).then(function (frags) {
      frags.forEach(function (frag) {
        if (!frag) return;
        var incoming = frag.querySelector(LIST);
        if (incoming) moveChildren(incoming, list, anchor);
      });
      if (pag) updateCounts(section, pag);
      window.dispatchEvent(new Event("resize"));

      /* Re-apply a few times: product cards reserve their height via aspect-ratio
         boxes, but fonts, swatches and any late layout can still shift things by
         a few pixels after the first frame. Any real user input cancels it so we
         never fight the shopper for control of the scrollbar. */
      if (typeof st.scrollY === "number" && st.scrollY > 0) {
        var target = st.scrollY;
        var attempts = 0;
        var cancel = function () { restoring = false; };
        ["wheel", "touchstart", "keydown"].forEach(function (ev) {
          window.addEventListener(ev, cancel, { once: true, passive: true });
        });
        var apply = function () {
          if (!restoring) return;
          window.scrollTo(0, target);
          if (++attempts < 4) window.setTimeout(apply, 160);
          else restoring = false;
        };
        window.requestAnimationFrame(apply);
      } else {
        restoring = false;
      }
    });
  }

  // -----------------------------------------------------------------------
  // Wiring
  // -----------------------------------------------------------------------
  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;

    var button = e.target.closest(BUTTON);
    if (button) {
      e.preventDefault();
      loadNext(button);
      return;
    }

    /* The "Next" link is the crawl path and the no-JS fallback, but for a shopper
       with JS it should behave exactly like the button rather than throwing away
       everything they have already loaded. */
    var nextLink = e.target.closest("[data-mb-load-next]");
    if (nextLink) {
      var wrap = nextLink.closest(PAGINATION);
      var btn = wrap && wrap.querySelector(BUTTON);
      if (btn) {
        e.preventDefault();
        loadNext(btn);
      }
    }
  });

  /* Remember where the shopper is, so returning from a product lands correctly.
     Recorded continuously (throttled) rather than on `pagehide`: Chrome has
     already reset window.scrollY to 0 by the time pagehide fires on a normal
     link navigation, which silently breaks the restore. */
  var scrollTimer = null;
  function rememberScroll() {
    var st = readState();
    if (st && st.page) writeState({ page: st.page, scrollY: window.scrollY });
  }
  window.addEventListener("scroll", function () {
    if (restoring || scrollTimer) return;
    scrollTimer = setTimeout(function () {
      scrollTimer = null;
      rememberScroll();
    }, 150);
  }, { passive: true });

  /* Capture-phase, so it runs before the browser starts navigating. This is the
     moment that actually matters — a shopper who scrolls and clicks a product
     inside the throttle window would otherwise be returned to a stale position. */
  document.addEventListener("click", function (e) {
    if (restoring) return;
    var link = e.target.closest ? e.target.closest("a[href]") : null;
    if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
    rememberScroll();
  }, true);

  window.addEventListener("popstate", function () {
    if (!restoring) rememberScroll();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", restoreIfReturning);
  } else {
    restoreIfReturning();
  }
})();